import { useEffect, useState } from "react";
import api from "../services/api";
import ZoomMtgEmbedded from "@zoom/meetingsdk/embedded";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { motion, AnimatePresence } from "framer-motion";
import { Calendar as CalendarIcon, Video, Clock, PlayCircle, Info, Plus, X } from "lucide-react";
import toast, { Toaster } from 'react-hot-toast';
import { jwtDecode } from "jwt-decode";

export default function LiveClasses() {
  const [classes, setClasses] = useState([]);
  const [courseMap, setCourseMap] = useState({});
  const [activeClass, setActiveClass] = useState(null);
  const [client, setClient] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [newSchedule, setNewSchedule] = useState({ courseId: "", time: "" });
  const [coursesList, setCoursesList] = useState([]);

  const currentUser = (() => {
    try {
      return jwtDecode(localStorage.getItem("token"));
    } catch { return { role: "student" }; }
  })();
  const userRole = currentUser.role || "student";

  const liveClassesEndpoint = userRole === "teacher" ? "/live/teacher" : "/live/my";

  const loadData = async () => {
    try {
      const [res, courseRes] = await Promise.all([
        api.get(liveClassesEndpoint),
        api.get("/courses")
      ]);
      setClasses(res.data);
      const map = Object.fromEntries(courseRes.data.map(c => [c.id, c.title]));
      setCourseMap(map);
      
      // If teacher, filter courses for the modal
      if (userRole === "teacher") {
         const userId = currentUser.id;
         setCoursesList(courseRes.data.filter(c => c.teacher_id === userId));
      } else if (userRole === "admin") {
         setCoursesList(courseRes.data);
      }
    } catch (err) {
      toast.error("Failed to load live sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredClasses = classes.filter((c) => {
    if (!c.scheduled_at) return false;
    const classDate = new Date(c.scheduled_at);
    return classDate.toDateString() === selectedDate.toDateString();
  });

  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const hasClass = classes.some(c => new Date(c.scheduled_at).toDateString() === date.toDateString());
      return hasClass ? <div className="mx-auto mt-1 w-1.5 h-1.5 rounded-full bg-red-500" /> : null;
    }
  };

  const handleScheduleClass = async (e) => {
    e.preventDefault();
    if (!newSchedule.courseId || !newSchedule.time) return toast.error("Select course & time");
    try {
      await api.post(`/live/create/${newSchedule.courseId}`, { scheduled_at: newSchedule.time });
      toast.success("Live class scheduled!");
      setScheduleModalOpen(false);
      setNewSchedule({ courseId: "", time: "" });
      loadData();
    } catch (err) {
      toast.error("Failed to schedule class");
    }
  };

  // 🚀 Start Meeting logic (keeping existing functionality)
  useEffect(() => {
    if (activeClass && activeClass.meeting_id) {
      // 📡 SKIP ZOOM SDK IF WEBRTC MODE
      const isWebRTC = activeClass.platform === "webrtc" || activeClass.platform === "youtube" || (activeClass.join_url && activeClass.join_url.includes("jit.si"));
      if (isWebRTC) return;

      const startZoom = async () => {
        try {
          const sigRes = await api.get(`/live/signature/${activeClass.meeting_id}?role=0`);
          const { signature, sdkKey } = sigRes.data;
          const zoomClient = client || ZoomMtgEmbedded.createClient();
          if (!client) setClient(zoomClient);
          const meetingElement = document.getElementById("meetingSDKElement");
          if (!meetingElement) return;

          await zoomClient.init({ zoomAppRoot: meetingElement, language: "en-US", patchJsMedia: true, leaveOnPageUnload: true });
          await zoomClient.join({ sdkKey, signature, meetingNumber: activeClass.meeting_id, password: activeClass.password, userName: "Student" });
        } catch (err) {
          toast.error("Failed to join meeting");
          setActiveClass(null);
        }
      };
      startZoom();
    }
  }, [activeClass, client]);

  if (loading) return (
    <div className="flex h-screen items-center justify-center dark:bg-slate-900">
      <div className="w-10 h-10 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="w-full">
      <Toaster position="top-center" />
      
      <main className="flex-1 space-y-8 p-4 sm:p-6 lg:p-10 min-w-0">
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white flex items-center gap-3">
              <CalendarIcon className="text-red-500" /> Live Class Schedule
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-2">Manage your time and join live learning sessions.</p>
          </div>
          {(userRole === "admin" || userRole === "teacher") && (
            <button 
              onClick={() => setScheduleModalOpen(true)}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg shadow-red-600/30 transition-all"
            >
              <Plus size={18} /> Schedule Session
            </button>
          )}
        </header>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 items-start">
          {/* Calendar Card */}
          <section className="card p-6 xl:sticky xl:top-24">
            <Calendar
              onChange={setSelectedDate}
              value={selectedDate}
              tileContent={tileContent}
              className="custom-calendar-modern"
            />
            <div className="mt-6 pt-6 border-t dark:border-slate-800">
              <div className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest">
                <Info size={14} /> Legend
              </div>
              <div className="mt-3 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span>Scheduled Session</span>
              </div>
            </div>
            <style>{`
              .custom-calendar-modern {
                width: 100% !important;
                border: none !important;
                font-family: inherit !important;
                background: transparent !important;
              }
              .react-calendar__navigation button {
                @apply text-slate-900 dark:text-white font-bold p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors;
              }
              .react-calendar__month-view__weekdays__weekday {
                @apply text-[10px] uppercase font-black text-slate-400 p-2 text-center;
              }
              .react-calendar__tile {
                @apply p-4 text-sm font-medium rounded-xl transition-all dark:text-slate-300;
              }
              .react-calendar__tile:hover {
                @apply bg-blue-50 dark:bg-slate-800;
              }
              .react-calendar__tile--active {
                @apply bg-blue-600 !text-white shadow-lg shadow-blue-600/30;
              }
              .react-calendar__tile--now {
                @apply bg-slate-100 dark:bg-slate-800 border-2 border-blue-500/20;
              }
            `}</style>
          </section>

          {/* Classes List */}
          <section className="xl:col-span-2 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold dark:text-white">
                Sessions for {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </h2>
              <span className="px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-lg text-xs font-bold">
                {filteredClasses.length} Scheduled
              </span>
            </div>

            <AnimatePresence mode="popLayout">
              {filteredClasses.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="card p-12 text-center flex flex-col items-center justify-center gap-4 bg-transparent border-dashed"
                >
                  <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-full text-slate-300 dark:text-slate-600">
                    <Video size={48} />
                  </div>
                  <div className="text-slate-500">No live sessions scheduled for this day.</div>
                </motion.div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredClasses.map((c) => (
                    <motion.div
                      layout key={c.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="card p-6 group hover:border-blue-500/50 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-6">
                        <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-2xl">
                          <Video size={24} />
                        </div>
                        <div className="text-right">
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Time</div>
                          <div className="text-sm font-bold dark:text-white flex items-center gap-2">
                            <Clock size={14} className="text-blue-500" />
                            {new Date(c.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>

                      <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 line-clamp-2">
                        {courseMap[c.course_id] || "Course Session"}
                      </h3>

                      <div className="flex items-center gap-3 mt-6">
                        <button
                          onClick={() => setActiveClass(c)}
                          className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-lg shadow-red-600/20 transition-all flex items-center justify-center gap-2"
                        >
                          <PlayCircle size={18} /> Join Now
                        </button>
                        {c.recording_url && (
                          <button
                            onClick={() => window.open(c.recording_url, "_blank")}
                            className="p-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-xl hover:bg-slate-200"
                          >
                            <Clock size={18} />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </AnimatePresence>
          </section>
        </div>
      </main>

      {/* ZOOM OVERLAY */}
      <AnimatePresence>
        {activeClass && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-[100] flex flex-col"
          >
            <div className="p-4 bg-slate-900 flex justify-between items-center text-white">
              <span className="font-bold flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                Live Streaming: {courseMap[activeClass.course_id]}
              </span>
              <button onClick={() => setActiveClass(null)} className="px-4 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-bold shadow-lg transition-all">Disconnect</button>
            </div>
            {
              (activeClass.platform === "webrtc" || (activeClass.join_url && activeClass.join_url.includes("jit.si")))
              ? <iframe 
                  src={activeClass.join_url} 
                  allow="camera; microphone; fullscreen; display-capture; autoplay" 
                  className="flex-1 w-full border-0 bg-slate-900"
                />
              : activeClass.platform === "youtube"
              ? <iframe
                  src={activeClass.join_url.replace("watch?v=", "embed/")}
                  allow="autoplay; encrypted-media; fullscreen"
                  className="flex-1 w-full border-0 bg-slate-900"
                />
              : <div id="meetingSDKElement" className="flex-1 bg-black" />
            }
          </motion.div>
        )}
      </AnimatePresence>
      {/* Schedule Modal */}
      {scheduleModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full shadow-2xl border dark:border-slate-800">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold dark:text-white">Schedule Live Session</h3>
              <button onClick={() => setScheduleModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleScheduleClass} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Select Course</label>
                <select
                  required
                  value={newSchedule.courseId}
                  onChange={e => setNewSchedule({ ...newSchedule, courseId: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">-- Choose Course --</option>
                  {coursesList.map(c => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Date & Time</label>
                <input
                  required
                  type="datetime-local"
                  value={newSchedule.time}
                  onChange={e => setNewSchedule({ ...newSchedule, time: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div className="pt-4 flex gap-3">
                <button type="button" onClick={() => setScheduleModalOpen(false)} className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors dark:text-white">Cancel</button>
                <button type="submit" className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors shadow-lg shadow-red-600/20">Schedule</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
